/**
 * Presentation helpers for Joe's monthly picks task. The task shows Joe's take (a short summary),
 * then the picks as a decision list, then his other options folded away. Joe's report text used to
 * list every pick a second time above the cards; a long report is now folded behind "Read Joe's
 * full note" and the take is written from the picks instead.
 */

/** The fields these helpers read from a community pick. */
export interface PickLike {
  id: string;
  name: string;
  categoryLabel: string;
  isNew?: boolean;
}

/** Joe's report fits as the take when it reads as a summary, not a list of picks. */
const TAKE_MAX_CHARS = 600;
const TAKE_MAX_LINES = 4;

const TITLE_LINE = /^\s*#*\s*\**\s*joe['’]s picks\b[^\n]*\n+/i;

export interface ScoutReportParts {
  /** Short enough to show as Joe's take, title line removed. */
  take: string | null;
  /** The whole report, for the "Read Joe's full note" fold, when it is too long to be the take. */
  fullNote: string | null;
}

export function splitScoutReport(body: string | null | undefined): ScoutReportParts {
  const text = (body ?? '').trim();
  if (!text) return { take: null, fullNote: null };
  const withoutTitle = text.replace(TITLE_LINE, '').trim();
  const lines = withoutTitle.split('\n').filter((line) => line.trim() !== '');
  if (withoutTitle && withoutTitle.length <= TAKE_MAX_CHARS && lines.length <= TAKE_MAX_LINES) {
    return { take: withoutTitle, fullNote: null };
  }
  return { take: null, fullNote: text };
}

function listNames(names: string[]): string {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** The take when Joe's own words are too long to be one: counts and the names worth starting with. */
export function fallbackTake(open: PickLike[]): string {
  if (open.length === 0) {
    return 'Every pick this month has a decision. Want someone else? Look at the other options below, or ask Joe in the thread.';
  }
  const people = open.length === 1 ? '1 pick' : `${open.length} picks`;
  const fresh = open.filter((p) => p.isNew);
  if (fresh.length === 0) {
    return `Joe has ${people} worth meeting this month, best fit first.`;
  }
  const named = fresh.slice(0, 3).map((p) => p.name);
  const more = fresh.length > named.length ? ` and ${fresh.length - named.length} more` : '';
  const isAre = fresh.length === 1 ? 'is' : 'are';
  return `Joe has ${people} worth meeting this month. ${fresh.length} ${isAre} new to your area: ${listNames(named)}${more}. Start there: new practices are choosing who they refer to now.`;
}

export interface PickFilter {
  key: string;
  label: string;
  count: number;
}

/** "All", "New in your area" when any are new, then the categories present, largest first. */
export function pickFilters(picks: PickLike[], maxCategories = 4): PickFilter[] {
  const filters: PickFilter[] = [{ key: 'all', label: 'All', count: picks.length }];
  const fresh = picks.filter((p) => p.isNew).length;
  if (fresh > 0 && fresh < picks.length) filters.push({ key: 'new', label: 'New in your area', count: fresh });
  const groups = groupByCategory(picks);
  if (groups.length > 1) {
    [...groups]
      .sort((a, b) => b.items.length - a.items.length)
      .slice(0, maxCategories)
      .forEach((g) => filters.push({ key: `cat:${g.label}`, label: g.label, count: g.items.length }));
  }
  return filters;
}

export function matchesFilter(pick: PickLike, key: string): boolean {
  if (key === 'all') return true;
  if (key === 'new') return !!pick.isNew;
  if (key.startsWith('cat:')) return pick.categoryLabel === key.slice(4);
  return true;
}

/** Picks grouped by category label, groups in first-seen order (the ranking). */
export function groupByCategory<T extends PickLike>(picks: T[]): Array<{ label: string; items: T[] }> {
  const groups: Array<{ label: string; items: T[] }> = [];
  for (const pick of picks) {
    const label = pick.categoryLabel || 'Other';
    const group = groups.find((g) => g.label === label);
    if (group) group.items.push(pick);
    else groups.push({ label, items: [pick] });
  }
  return groups;
}

/** Two-letter initials for a pick's avatar: "Dr. Christina Arias" → "CA". */
export function pickInitials(name: string): string {
  const words = name
    .replace(/^(dr|mr|mrs|ms|mx)\.?\s+/i, '')
    .split(/\s+/)
    .filter((w) => /^[a-z0-9]/i.test(w) && !/^(llc|pllc|inc|pc|ltd)\.?$/i.test(w));
  if (words.length === 0) return '?';
  const first = words[0][0];
  const last = words.length > 1 ? words[words.length - 1][0] : '';
  return `${first}${last}`.toUpperCase();
}
